"use client";

import { findFirstUrl } from "@/lib/linkDetect";
import { useResolvedLinkPreview } from "@/lib/hooks/useResolvedLinkPreview";
import { LINK_CARD_FRAME, LinkCardSkeleton, ThetaShareCard, isDrawableLinkPreview } from "./LinkPreviewCard";

/**
 * The preview the author sees BEFORE they post.
 *
 * Two behaviours make this feel like every other composer people use:
 *
 *  1. **It follows the text.** Remove the link and the card goes with it —
 *     the card is derived from what is written, never a thing that outlives it.
 *  2. **Dismissing is per-link, not forever.** Closing the card suppresses
 *     THAT url; pasting a different one previews again. A single sticky
 *     "dismissed" flag would silently break the next paste, which reads as the
 *     feature being broken rather than as a choice the author made.
 *
 * THE STATE IS THE CALLER'S — OSO-568. It used to be a `useState` in here, and
 * that is exactly why the button was decorative: the dismissal never left the
 * component, so the published post drew the card again. It is the same value
 * with the same per-link meaning, held one level up and sent with the post.
 */
export function ComposerLinkPreview({
  text,
  dismissed,
  onDismiss,
}: {
  text: string;
  /**
   * The url the author dismissed, not a boolean — which is what makes
   * behaviour 2 fall out for free. Typing on past a dismissed link does not
   * resurrect it because the url has not changed; replacing it does, because
   * the new url is not the dismissed one.
   *
   * DERIVED DURING RENDER, NOT RESET IN AN EFFECT. This used to be reset to
   * null whenever the url changed, which React lints as a cascading render —
   * and it was doing nothing, since a stale dismissal can only ever differ
   * from the current url, and the comparison below already handles that.
   */
  dismissed: string | null;
  /** Called with the url whose card was just closed. */
  onDismiss: (url: string) => void;
}) {
  const url = findFirstUrl(text);

  const active = url && url !== dismissed ? url : null;
  const { data, isLoading } = useResolvedLinkPreview(active);

  if (!active) return null;
  if (isLoading) return <div className="mt-2"><LinkCardSkeleton /></div>;
  if (!data || data.kind === "none") return null;
  /**
   * A card this build cannot draw takes its chrome with it (OSO-616). The
   * frame and the dismiss button below are unconditional, so a `null` card
   * would leave the author a zero-height box with a floating "Fjern
   * forhåndsvisning" over whatever sits beneath it, dismissing a preview that
   * is not on screen.
   */
  if (!isDrawableLinkPreview(data.preview)) return null;

  return (
    /* The frame again, on the positioning parent: the card centres itself
       inside whatever holds it, so a full-width parent would leave the dismiss
       button floating in the margin beside the card rather than on it. */
    <div className={`${LINK_CARD_FRAME} relative mt-2`}>
      <ThetaShareCard preview={data.preview} />
      <button
        type="button"
        onClick={() => onDismiss(active)}
        aria-label="Fjern forhåndsvisning"
        className="absolute right-2 top-2 rounded-full bg-background/90 p-1.5 text-muted-foreground shadow-sm transition-colors hover:bg-background hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
